import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BananaApi } from "../api/BananaApi";
import { RELAY_INTENTS } from "../relay/RelayPrompts";
import { BananaType } from "../types/BananaType";

const LandingPage = () => {
  const [totalTossed, setTotalTossed] = useState<number | null>(null);
  const featuredSeasonal = BananaType.getFeaturedSeasonal();

  useEffect(() => {
    BananaApi.getBananaverseStats()
      .then((stats) => setTotalTossed(stats.totalSent))
      .catch((error) => console.error("Failed to fetch banana stats:", error));
  }, []);

  return (
    <>
      <title>Tuttobanana | Tiny Gift. Big Feeling.</title>
      <meta name="description" content="Spread joy. Share love. Everywhere. Send someone a free private banana and watch the feeling travel around the world." />

      <main className="overflow-hidden bg-white text-gray-900">
        <section className="relative border-y border-gray-200 bg-yellow-300 px-6 py-16 sm:px-12 sm:py-24">
          <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[44px] border-yellow-200/70" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="max-w-3xl text-center lg:text-left">
              <p className="mb-5 text-sm font-bold uppercase tracking-[0.22em] text-gray-800">Private. Personal. Free.</p>
              <h1 className="text-5xl font-black leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">
                Tiny Gift.<span className="block">Big Feeling.</span>
              </h1>
              <p className="mx-auto mt-7 max-w-2xl text-2xl font-black leading-tight text-gray-900 sm:text-3xl lg:mx-0">Spread joy. Share love. Everywhere.</p>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-800 sm:text-xl lg:mx-0">
                Send someone a private banana with a message that matters. See when it lands, then watch the joy travel from person to person around the world.
              </p>
              <div className="mt-9 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
                <Link to="/tuttobanana" className="inline-flex min-h-14 w-full items-center justify-center rounded-full bg-gray-900 px-8 py-4 text-base font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-black sm:w-auto">
                  Toss a Banana — It&apos;s Free <span className="ml-2" aria-hidden="true">→</span>
                </Link>
                <a href="#how-it-works" className="px-5 py-3 font-bold text-gray-900 underline decoration-2 underline-offset-4">See how it works</a>
              </div>
              <p className="mt-5 text-sm font-semibold text-gray-700">No account setup. No banana credits. Just send.</p>
            </div>

            <div className="relative mx-auto w-full max-w-md">
              <div className="absolute inset-5 translate-x-4 translate-y-4 rounded-[2rem] bg-gray-900" />
              <div className="relative rounded-[2rem] border-2 border-gray-900 bg-white p-7 shadow-xl sm:p-9">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-600">A banana is waiting</p>
                    <h2 className="mt-2 text-3xl font-black">Someone thought of you.</h2>
                  </div>
                  <img src="/logos/logo512.png" alt="A smiling Tuttobanana" className="h-28 w-28 object-contain" />
                </div>
                <div className="mt-7 rounded-2xl border border-yellow-300 bg-yellow-50 p-5 text-lg font-medium leading-relaxed">“You crossed my mind today, and I wanted you to know you matter.”</div>
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-5 text-sm font-bold">
                  <span>Open the feeling</span><span className="rounded-full bg-yellow-400 px-4 py-2">Unwrap 🍌</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-8 bg-gray-50 px-6 py-20 sm:px-12">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-yellow-600">One minute can matter</p>
              <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Send a feeling. Start a journey.</h2>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {[
                ["1", "Say something meaningful", "Choose a banana and add a message for someone who matters."],
                ["2", "Send it anywhere in the world", "Share it privately through text, email, WhatsApp, or anywhere you talk."],
                ["3", "Watch the joy spread", "See when it lands—and how many people, places, and generations it reaches."],
              ].map(([number, title, body]) => (
                <article key={number} className="rounded-3xl border border-gray-200 bg-white p-7 shadow-sm">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-yellow-400 text-lg font-black">{number}</span>
                  <h3 className="mt-6 text-2xl font-black">{title}</h3><p className="mt-3 leading-relaxed text-gray-600">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {featuredSeasonal && <section className="bg-white px-6 py-16 sm:px-12">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-8 rounded-[2rem] border-2 border-yellow-300 bg-yellow-50 p-8 shadow-sm sm:flex-row sm:p-10">
            <img src={featuredSeasonal.img} alt={featuredSeasonal.title} className="h-52 w-52 shrink-0 object-contain" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-700">Seasonal banana</p>
              <h2 className="mt-2 text-3xl font-black">{featuredSeasonal.title}</h2>
              <p className="mt-3 text-lg text-gray-600">{featuredSeasonal.description}</p>
              <Link to={`/tuttobanana?banana=${featuredSeasonal.key}`} className="mt-6 inline-flex rounded-full bg-gray-900 px-6 py-3 font-bold text-white">Send this banana →</Link>
            </div>
          </div>
        </section>}

        <section className="bg-white px-6 py-20 sm:px-12">
          <div className="mx-auto max-w-6xl">
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-yellow-600">Start with a person</p>
              <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Who are you thinking about?</h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">Pick a reason. We&apos;ll help you find the words—and you can make them completely your own.</p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {RELAY_INTENTS.map((intent) => (
                <Link key={intent.key} to={`/tuttobanana?intent=${intent.key}`} className="group flex min-h-44 flex-col rounded-3xl border-2 border-gray-200 bg-white p-7 transition hover:-translate-y-1 hover:border-yellow-400 hover:shadow-lg">
                  <span className="text-4xl" aria-hidden="true">{intent.emoji}</span>
                  <h3 className="mt-5 text-xl font-black">{intent.title}</h3><p className="mt-1 text-gray-600">{intent.subtitle}</p>
                  <span className="mt-auto pt-5 font-black text-yellow-600 group-hover:text-yellow-700">Send this feeling →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-900 px-6 py-20 text-white sm:px-12">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-yellow-400">The Bananaverse</p>
              <h2 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">Spread joy. Share love. Everywhere.</h2>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-300">Every banana gets a Passport. When someone opens it, reacts, or keeps it moving, its journey grows across the world—while names and private messages stay private.</p>
              <ul className="mt-8 space-y-4 font-semibold">
                <li className="flex gap-3"><span className="text-yellow-400">✓</span> Know when your feeling landed</li>
                <li className="flex gap-3"><span className="text-yellow-400">✓</span> See stops, miles, countries, and generations</li>
                <li className="flex gap-3"><span className="text-yellow-400">✓</span> Watch one thoughtful moment become many</li>
              </ul>
            </div>
            <JourneyGlobe />
          </div>
        </section>

        <section className="bg-yellow-300 px-6 py-20 text-center sm:px-12">
          <div className="mx-auto max-w-3xl">
            {totalTossed !== null && totalTossed > 0 && <p className="mb-5 text-sm font-black uppercase tracking-[0.2em] text-gray-700">🍌 {totalTossed.toLocaleString("en-US")} bananas tossed and counting</p>}
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl">Someone is worth one minute.</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-gray-800">Tuttobanana exists to make emotional connection effortless and contagious—everywhere.</p>
            <Link to="/tuttobanana" className="mt-8 inline-flex min-h-14 items-center justify-center rounded-full bg-gray-900 px-9 py-4 font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-black">Start a Journey <span className="ml-2" aria-hidden="true">→</span></Link>
          </div>
        </section>

        <section className="border-t border-gray-200 bg-white px-6 py-10 text-center"><p className="text-gray-600">Questions? <strong className="text-gray-900">hello@tuttobanana.com</strong></p></section>
      </main>
    </>
  );
};

export default LandingPage;

const JourneyGlobe = () => (
  <div className="rounded-[2rem] bg-white p-5 text-gray-900 shadow-2xl sm:p-7">
    <div className="flex items-center justify-between gap-4 px-2">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-600">A feeling in motion</p>
        <h3 className="mt-1 text-2xl font-black">Watch the joy spread</h3>
      </div>
      <span className="rounded-full bg-yellow-400 px-4 py-2 text-sm font-black">🍌 LIVE</span>
    </div>

    <svg className="mt-4 h-auto w-full" viewBox="0 0 520 360" role="img" aria-label="Illustration of bananas traveling between locations around the world">
      <defs>
        <clipPath id="globe-clip"><circle cx="260" cy="174" r="137" /></clipPath>
        <linearGradient id="ocean" x1="0" x2="1" y1="0" y2="1"><stop stopColor="#e0f2fe" /><stop offset="1" stopColor="#bae6fd" /></linearGradient>
      </defs>
      <circle cx="260" cy="174" r="143" fill="#facc15" opacity="0.25" />
      <circle cx="260" cy="174" r="137" fill="url(#ocean)" stroke="#111827" strokeWidth="4" />
      <g clipPath="url(#globe-clip)" fill="none" stroke="#7dd3fc" strokeWidth="1.5" opacity="0.75">
        <ellipse cx="260" cy="174" rx="137" ry="45" /><ellipse cx="260" cy="174" rx="137" ry="88" />
        <ellipse cx="260" cy="174" rx="52" ry="137" /><ellipse cx="260" cy="174" rx="96" ry="137" />
        <path d="M123 174h274" />
      </g>
      <g clipPath="url(#globe-clip)" fill="#fde047" stroke="#ca8a04" strokeWidth="2">
        <path d="M142 102l32-29 42 4 20 22-18 17-26-8-16 18-25-5-20 13-13-17z" />
        <path d="M206 139l23 12 9 27-13 18-5 45-18 28-13-48 5-26-12-30z" />
        <path d="M280 89l26-17 48 8 24 21-9 22-32 3-12 19-30-4-21-22z" />
        <path d="M299 145l43-10 31 18-5 22-24 6-12 43-22 25-16-47-18-27z" />
        <path d="M366 231l30-9 23 18-16 20-35-4z" />
      </g>
      <g fill="none" stroke="#f59e0b" strokeLinecap="round" strokeWidth="4">
        <path d="M169 119Q251 35 329 120" strokeDasharray="7 8" />
        <path d="M329 120Q412 146 385 239" strokeDasharray="7 8" />
        <path d="M169 119Q220 209 315 198" strokeDasharray="7 8" />
      </g>
      {[[169,119], [329,120], [385,239], [315,198]].map(([cx, cy], index) => (
        <g key={`${cx}-${cy}`}>
          <circle cx={cx} cy={cy} r="12" fill="#fef3c7" stroke="#111827" strokeWidth="3" />
          <text x={cx} y={cy + 6} textAnchor="middle" fontSize="17">{index === 0 ? "●" : "🍌"}</text>
        </g>
      ))}
      <text x="260" y="337" textAnchor="middle" fill="#4b5563" fontSize="14" fontWeight="700">One message. A world of possible connection.</text>
    </svg>

    <div className="grid grid-cols-3 gap-3">
      {[["7", "people"], ["3", "countries"], ["2", "generations"]].map(([value, label]) => (
        <div key={label} className="rounded-2xl bg-gray-100 p-3 text-center"><div className="text-xl font-black">{value}</div><div className="text-xs font-bold uppercase tracking-wide text-gray-500">{label}</div></div>
      ))}
    </div>
    <p className="mt-4 text-center text-xs text-gray-500">Illustrative journey. Locations are always approximate.</p>
  </div>
);
